// LOS SITIOS DE FIRMA SOBRE EL PDF — el editor de la agente y lo que ve el
// cliente (Lissette 2026-10-23: firmar sobre el PDF original "como DocuSign").
//
// Las DOS pantallas pintan con las MISMAS piezas de este archivo (la página del
// PDF y la caja de cada sitio), igual que PaginaDelDocumento.jsx para las
// plantillas de texto: si la agente ve la caja en un sitio y el cliente en otro,
// la vista previa miente (E-317, regla 208).
//
// Las cajas van en FRACCIONES de la página (crm-app/camposSobreElPdf.js), así
// que se pintan con porcentajes encima del lienzo y valen a cualquier tamaño de
// pantalla. Quien estampa de verdad es el servidor
// (deskte-api/src/utils/camposSobreElPdf.ts), con la caja y el giro de la página.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Download, FileText, Trash2, Wand2 } from 'lucide-react';
import { cargarPdfJs } from '../cdnVendor.js';
import {
  ETIQUETAS_DE_CAMPO_PDF, TIPOS_DE_CAMPO_PDF, MAX_CAMPOS_PDF, nuevoCampo, limitarCaja, seSolapan, idDeCampo, cuentaPorTipo,
  etiquetaDeCampo,
} from '../camposSobreElPdf.js';
import { detectarSitiosDeFirma, itemsDeTextoDePdfjs } from '../detectarSitiosDeFirma.js';

/** Más páginas que esto no se pintan (un formulario de aseguradora rara vez pasa de 30). */
export const MAX_PAGINAS_DEL_EDITOR = 60;

/**
 * Abre el PDF con pdf.js, desde un archivo del equipo (`archivo`) o desde un
 * enlace de lectura (`url`). Se cierra solo al desmontar.
 */
function usePdf(archivo, url) {
  const [estado, setEstado] = useState({ pdf: null, error: null, cargando: true });
  useEffect(() => {
    let vivo = true;
    let doc = null;
    setEstado({ pdf: null, error: null, cargando: true });
    (async () => {
      try {
        const lib = await cargarPdfJs();
        let datos;
        if (archivo) datos = new Uint8Array(await archivo.arrayBuffer());
        else if (url) {
          const r = await fetch(url);
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          datos = new Uint8Array(await r.arrayBuffer());
        } else throw new Error('no-document');
        doc = await lib.getDocument({ data: datos }).promise;
        if (vivo) setEstado({ pdf: doc, error: null, cargando: false });
        else doc.destroy();
      } catch (e) {
        if (vivo) setEstado({ pdf: null, error: (e && e.message) || 'pdf-error', cargando: false });
      }
    })();
    return () => {
      vivo = false;
      if (doc) { try { doc.destroy(); } catch { /* ya estaba cerrado */ } }
    };
  }, [archivo, url]);
  return estado;
}

/**
 * Una página del PDF en un lienzo, con lo que se le ponga encima (`children`).
 * Se pinta cuando se acerca a la vista: un PDF de 30 páginas no se dibuja
 * entero de golpe en un teléfono.
 */
function PaginaDelPdf({ pdf, numero, onTamano, onPointerDown, cursor, children }) {
  const cajaRef = useRef(null);
  const lienzoRef = useRef(null);
  const [proporcion, setProporcion] = useState(11 / 8.5);
  const [visible, setVisible] = useState(false);

  // Medidas de la página en puntos (escala 1, con su giro): las necesitan las
  // cajas nuevas para nacer con un tamaño de papel de verdad.
  useEffect(() => {
    let vivo = true;
    pdf.getPage(numero).then((p) => {
      if (!vivo) return;
      const vp = p.getViewport({ scale: 1 });
      setProporcion(vp.height / vp.width);
      if (onTamano) onTamano(numero, { ancho: vp.width, alto: vp.height });
    }).catch(() => { /* la página se queda en blanco; el resto sigue */ });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdf, numero]);

  useEffect(() => {
    const el = cajaRef.current;
    if (!el || visible) return undefined;
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return undefined; }
    const obs = new IntersectionObserver((entradas) => {
      if (entradas.some((e) => e.isIntersecting)) { setVisible(true); obs.disconnect(); }
    }, { rootMargin: '800px 0px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!visible) return undefined;
    let vivo = true;
    let tarea = null;
    pdf.getPage(numero).then((p) => {
      const lienzo = lienzoRef.current;
      if (!vivo || !lienzo) return null;
      const base = p.getViewport({ scale: 1 });
      const vp = p.getViewport({ scale: Math.min(2, 1100 / base.width) });
      lienzo.width = Math.floor(vp.width);
      lienzo.height = Math.floor(vp.height);
      tarea = p.render({ canvasContext: lienzo.getContext('2d'), viewport: vp });
      return tarea.promise;
    }).catch(() => { /* cancelado al desmontar, o página rota: queda en blanco */ });
    return () => { vivo = false; if (tarea) { try { tarea.cancel(); } catch { /* ya terminó */ } } };
  }, [pdf, numero, visible]);

  return (
    <div ref={cajaRef} data-pagina={numero} onPointerDown={onPointerDown}
      style={{ position: 'relative', width: '100%', paddingTop: `${proporcion * 100}%`, background: '#fff', boxShadow: '0 4px 18px rgba(0,0,0,.35)', borderRadius: 4, marginBottom: 18, cursor: cursor || 'default', userSelect: 'none' }}>
      <canvas ref={lienzoRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block', borderRadius: 4 }} />
      {children}
    </div>
  );
}

/**
 * La caja de un sitio. En el editor se mueve (arrastrando), se cambia de tamaño
 * (la esquina de abajo a la derecha) y se borra (×). En la página del cliente
 * solo se ve, con lo que va a salir dentro si ya lo ha escrito.
 */
function CajaDeCampo({ campo, lang, contenido, seleccionada, onMover, onCambiarTamano, onBorrar }) {
  const e = ETIQUETAS_DE_CAMPO_PDF[campo.tipo] || ETIQUETAS_DE_CAMPO_PDF.firma;
  const nombre = etiquetaDeCampo(campo, lang);
  const editable = !!onMover;
  return (
    <div onPointerDown={onMover} title={nombre}
      style={{
        position: 'absolute', left: `${campo.x * 100}%`, top: `${campo.y * 100}%`, width: `${campo.w * 100}%`, height: `${campo.h * 100}%`,
        boxSizing: 'border-box', border: `${seleccionada ? 2 : 1.5}px ${editable ? 'solid' : 'dashed'} ${e.color}`, background: `${e.color}26`,
        borderRadius: 3, cursor: editable ? 'move' : 'default', touchAction: editable ? 'none' : 'auto',
        display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'visible', zIndex: seleccionada ? 3 : 2,
      }}>
      <span style={{ position: 'absolute', left: -1.5, bottom: '100%', fontSize: 10, fontWeight: 700, lineHeight: '14px', color: '#fff', background: e.color, padding: '0 5px', borderRadius: '3px 3px 0 0', whiteSpace: 'nowrap', pointerEvents: 'none', fontFamily: 'system-ui,sans-serif' }}>
        {nombre}
      </span>
      {contenido}
      {editable && (
        <>
          <button type="button" onPointerDown={(ev) => ev.stopPropagation()} onClick={onBorrar}
            title={lang === 'es' ? 'Quitar este sitio' : 'Remove this spot'}
            style={{ position: 'absolute', top: -9, right: -9, width: 18, height: 18, borderRadius: '50%', border: 'none', background: '#ef4444', color: '#fff', fontSize: 12, lineHeight: '18px', padding: 0, cursor: 'pointer', fontWeight: 800 }}>
            ×
          </button>
          <div onPointerDown={onCambiarTamano} title={lang === 'es' ? 'Arrastra para cambiar el tamaño' : 'Drag to resize'}
            style={{ position: 'absolute', right: -6, bottom: -6, width: 12, height: 12, borderRadius: 3, background: e.color, border: '2px solid #fff', cursor: 'nwse-resize', touchAction: 'none' }} />
        </>
      )}
    </div>
  );
}

/** Lee el texto con posiciones de cada página y propone sitios. */
async function proponerSitios(pdf) {
  const paginas = [];
  let conTexto = false;
  for (let p = 1; p <= Math.min(pdf.numPages, MAX_PAGINAS_DEL_EDITOR); p++) {
    const pagina = await pdf.getPage(p);
    const vp = pagina.getViewport({ scale: 1 });
    const contenido = await pagina.getTextContent();
    const items = itemsDeTextoDePdfjs(contenido.items, vp.transform);
    if (items.length) conTexto = true;
    paginas.push({ page: p, ancho: vp.width, alto: vp.height, items });
  }
  return { propuestos: detectarSitiosDeFirma(paginas), conTexto };
}

/**
 * EL EDITOR DE SITIOS (pantalla completa). La agente ve el PDF, pulsa un tipo y
 * hace clic donde va (las veces que quiera), arrastra, cambia el tamaño, borra,
 * o pide "Detectar automáticamente". Nada se guarda hasta "Guardar sitios".
 *
 * `detectarAlAbrir`: un PDF recién subido arranca con la propuesta hecha — pero
 * es solo eso, una propuesta en pantalla que ella revisa antes de guardar.
 */
export function EditorDeSitiosDelPdf({ archivo, url, campos: iniciales, lang, detectarAlAbrir, onGuardar, onCancelar }) {
  const es = lang === 'es';
  const { pdf, error, cargando } = usePdf(archivo, url);
  const [campos, setCampos] = useState(() => (Array.isArray(iniciales) ? iniciales.map(limitarCaja) : []));
  const camposRef = useRef(campos);
  camposRef.current = campos;
  const [herramienta, setHerramienta] = useState(null);
  const [seleccion, setSeleccion] = useState(null);
  const [detectando, setDetectando] = useState(false);
  const [aviso, setAviso] = useState('');
  const tamanos = useRef({});
  const arrastre = useRef(null);
  const yaDetecto = useRef(false);
  const seleccionRef = useRef(null);
  seleccionRef.current = seleccion;

  const anotarTamano = useCallback((numero, t) => { tamanos.current[numero] = t; }, []);

  const detectar = useCallback(async () => {
    if (!pdf) return;
    setDetectando(true);
    try {
      const { propuestos, conTexto } = await proponerSitios(pdf);
      const actuales = camposRef.current;
      const nuevos = [];
      for (const c of propuestos) {
        if (actuales.length + nuevos.length >= MAX_CAMPOS_PDF) break;
        if (![...actuales, ...nuevos].some((o) => seSolapan(o, c))) nuevos.push({ ...c, id: idDeCampo(c.tipo) });
      }
      setCampos([...actuales, ...nuevos]);
      if (!conTexto) {
        setAviso(es
          ? 'Este PDF no tiene texto que leer (¿es un escaneo?). Coloca los sitios a mano con los botones de arriba.'
          : 'This PDF has no readable text (is it a scan?). Place the spots by hand with the buttons above.');
      } else if (!nuevos.length) {
        setAviso(es
          ? 'No encontré sitios nuevos de firma. Colócalos a mano con los botones de arriba.'
          : 'No new signature spots found. Place them by hand with the buttons above.');
      } else {
        setAviso(es
          ? `Propuse ${nuevos.length} sitio(s). REVÍSALOS: mueve, cambia el tamaño o borra los que no van, y añade los que falten.`
          : `I proposed ${nuevos.length} spot(s). REVIEW them: move, resize or delete the wrong ones, and add any missing.`);
      }
    } catch (e) {
      setAviso((es ? 'No se pudo leer el PDF para detectar: ' : 'Could not read the PDF to detect: ') + ((e && e.message) || ''));
    } finally {
      setDetectando(false);
    }
  }, [pdf, es]);

  useEffect(() => {
    if (pdf && detectarAlAbrir && !yaDetecto.current) { yaDetecto.current = true; detectar(); }
  }, [pdf, detectarAlAbrir, detectar]);

  // Arrastrar y cambiar de tamaño: se escucha en la ventana para que el
  // puntero pueda salirse de la caja sin soltarla.
  useEffect(() => {
    const mover = (ev) => {
      const a = arrastre.current;
      if (!a) return;
      const dx = (ev.clientX - a.x0) / a.ancho;
      const dy = (ev.clientY - a.y0) / a.alto;
      setCampos((cs) => cs.map((c) => {
        if (c.id !== a.id) return c;
        if (a.modo === 'mover') {
          return limitarCaja({ ...a.c0, x: Math.min(Math.max(a.c0.x + dx, 0), 1 - a.c0.w), y: Math.min(Math.max(a.c0.y + dy, 0), 1 - a.c0.h) });
        }
        return limitarCaja({ ...a.c0, w: a.c0.w + dx, h: a.c0.h + dy });
      }));
    };
    const soltar = () => { arrastre.current = null; };
    const tecla = (ev) => {
      if (ev.key === 'Escape') setHerramienta(null);
      const escribiendo = ev.target && (/^(INPUT|TEXTAREA|SELECT)$/.test(ev.target.tagName) || ev.target.isContentEditable);
      if ((ev.key === 'Delete' || ev.key === 'Backspace') && seleccionRef.current && !escribiendo) {
        const id = seleccionRef.current;
        setCampos((cs) => cs.filter((c) => c.id !== id));
        setSeleccion(null);
      }
    };
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
    window.addEventListener('keydown', tecla);
    return () => {
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
      window.removeEventListener('keydown', tecla);
    };
  }, []);

  const empezarArrastre = (ev, campo, modo) => {
    ev.stopPropagation();
    ev.preventDefault();
    const pagina = ev.currentTarget.closest('[data-pagina]');
    if (!pagina) return;
    const r = pagina.getBoundingClientRect();
    arrastre.current = { id: campo.id, modo, x0: ev.clientX, y0: ev.clientY, c0: campo, ancho: r.width || 1, alto: r.height || 1 };
    setSeleccion(campo.id);
  };

  const ponerEnLaPagina = (ev, numero) => {
    if (!herramienta) { setSeleccion(null); return; }
    if (camposRef.current.length >= MAX_CAMPOS_PDF) {
      setAviso(es ? `Como mucho ${MAX_CAMPOS_PDF} sitios por documento.` : `At most ${MAX_CAMPOS_PDF} spots per document.`);
      return;
    }
    const r = ev.currentTarget.getBoundingClientRect();
    const t = tamanos.current[numero] || { ancho: 612, alto: 792 };
    const c = nuevoCampo(herramienta, numero, (ev.clientX - r.left) / r.width, (ev.clientY - r.top) / r.height, t.ancho, t.alto);
    setCampos((cs) => [...cs, c]);
    setSeleccion(c.id);
  };

  const cuenta = cuentaPorTipo(campos);
  const paginas = pdf ? Math.min(pdf.numPages, MAX_PAGINAS_DEL_EDITOR) : 0;
  const boton = (activo, color) => ({
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '.4rem .75rem', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
    fontSize: '.74rem', fontWeight: 700, border: `1px solid ${color}`, background: activo ? color : 'transparent', color: activo ? '#04121f' : color,
  });

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 10050, background: 'rgba(3,10,20,.92)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: 'var(--c-surface,#0d1c2e)', borderBottom: '1px solid var(--c-border,#1a3550)', padding: '.7rem 1rem', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '.5rem' }}>
        <div style={{ fontWeight: 800, color: 'var(--c-white,#eef6ff)', fontSize: '.9rem', marginRight: '.5rem' }}>
          ✍️ {es ? 'Sitios de firma en el PDF' : 'Signature spots on the PDF'}
        </div>
        {TIPOS_DE_CAMPO_PDF.map((tipo) => {
          const e = ETIQUETAS_DE_CAMPO_PDF[tipo];
          return (
            <button key={tipo} type="button" onClick={() => setHerramienta((h) => (h === tipo ? null : tipo))} style={boton(herramienta === tipo, e.color)}
              title={es ? 'Pulsa y luego haz clic en la página donde va (las veces que quieras). Esc para terminar.' : 'Press, then click on the page where it goes (as many times as you want). Esc to stop.'}>
              + {es ? e.es : e.en} {cuenta[tipo] ? `(${cuenta[tipo]})` : ''}
            </button>
          );
        })}
        <button type="button" onClick={detectar} disabled={!pdf || detectando} style={{ ...boton(false, '#00d4ff'), opacity: !pdf || detectando ? 0.5 : 1 }}>
          <Wand2 size={13} /> {detectando ? (es ? 'Buscando…' : 'Looking…') : (es ? 'Detectar automáticamente' : 'Detect automatically')}
        </button>
        {campos.length > 0 && (
          <button type="button" onClick={() => { setCampos([]); setSeleccion(null); }} style={boton(false, '#ef4444')}>
            <Trash2 size={13} /> {es ? 'Borrar todos' : 'Clear all'}
          </button>
        )}
        <div style={{ flex: 1 }} />
        <button type="button" onClick={onCancelar} className="dkt-btn-s">{es ? 'Cancelar' : 'Cancel'}</button>
        <button type="button" onClick={() => onGuardar(campos.map(limitarCaja))} disabled={!pdf}
          style={{ padding: '.5rem 1.1rem', borderRadius: 9, border: 'none', background: 'linear-gradient(135deg,#0099cc,#0055aa)', color: '#fff', fontWeight: 800, fontSize: '.8rem', cursor: pdf ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }}>
          {es ? `Guardar sitios (${campos.length})` : `Save spots (${campos.length})`}
        </button>
      </div>
      <div style={{ padding: '.45rem 1rem', fontSize: '.72rem', color: 'var(--c-text2,#7aa3c0)', background: 'var(--c-surface2,#112538)', display: 'flex', gap: '.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <span>
          {herramienta
            ? (es ? `Haz clic en la página donde va «${ETIQUETAS_DE_CAMPO_PDF[herramienta].es}». Esc para terminar.` : `Click on the page where "${ETIQUETAS_DE_CAMPO_PDF[herramienta].en}" goes. Esc to stop.`)
            : (es ? 'El cliente firma, escribe sus iniciales y su nombre UNA vez, y salen en cada sitio. Arrastra para mover; la esquina cambia el tamaño.' : 'The client signs, types initials and name ONCE, and they appear in every spot. Drag to move; the corner resizes.')}
        </span>
        {aviso && <strong style={{ color: '#fbbf24' }}>{aviso}</strong>}
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: '1.5rem 1rem' }}>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          {cargando && <div style={{ color: '#7aa3c0', textAlign: 'center', padding: '3rem' }}>{es ? 'Abriendo el PDF…' : 'Opening the PDF…'}</div>}
          {error && <div style={{ color: '#ff3d6b', textAlign: 'center', padding: '3rem' }}>{(es ? 'No se pudo abrir el PDF: ' : 'Could not open the PDF: ') + error}</div>}
          {pdf && pdf.numPages > MAX_PAGINAS_DEL_EDITOR && (
            <div style={{ color: '#fbbf24', fontSize: '.75rem', marginBottom: '.75rem' }}>
              {es ? `Solo se muestran las primeras ${MAX_PAGINAS_DEL_EDITOR} páginas.` : `Only the first ${MAX_PAGINAS_DEL_EDITOR} pages are shown.`}
            </div>
          )}
          {Array.from({ length: paginas }, (_, i) => i + 1).map((numero) => (
            <div key={numero}>
              <div style={{ fontSize: '.65rem', color: '#7aa3c0', margin: '0 0 .3rem' }}>{es ? 'Página' : 'Page'} {numero}</div>
              <PaginaDelPdf pdf={pdf} numero={numero} onTamano={anotarTamano} cursor={herramienta ? 'crosshair' : 'default'}
                onPointerDown={(ev) => ponerEnLaPagina(ev, numero)}>
                {campos.filter((c) => c.page === numero).map((c) => (
                  <CajaDeCampo key={c.id} campo={c} lang={lang} seleccionada={seleccion === c.id}
                    onMover={(ev) => empezarArrastre(ev, c, 'mover')}
                    onCambiarTamano={(ev) => empezarArrastre(ev, c, 'tamano')}
                    onBorrar={() => { setCampos((cs) => cs.filter((x) => x.id !== c.id)); setSeleccion(null); }} />
                ))}
              </PaginaDelPdf>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Lo que sale dentro de cada caja en la página del cliente, si ya lo escribió. */
function vistaDelValor(campo, valores) {
  const v = valores || {};
  const estilo = { fontSize: 'clamp(8px, 1.5vw, 15px)', color: '#0a1628', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', padding: '0 3px' };
  if (campo.tipo === 'iniciales' && v.iniciales) return <span style={{ ...estilo, fontFamily: v.fuente || 'cursive', fontSize: 'clamp(10px, 2vw, 20px)' }}>{v.iniciales}</span>;
  if (campo.tipo === 'nombre' && v.nombre) return <span style={estilo}>{v.nombre}</span>;
  if (campo.tipo === 'fecha') return <span style={estilo}>{new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}</span>;
  // Los datos del agente ya vienen escritos; los del cliente salen de su ficha al firmar.
  // En la vista previa de la agente llegan en `valores.datos` (cliente elegido y su perfil).
  const dato = campo.tipo === 'dato' ? (campo.valor || (v.datos || {})[campo.clave] || '') : '';
  if (dato) return <span style={estilo}>{dato}</span>;
  if (campo.tipo === 'marca' && campo.pregunta && (v.respuestas || {})[campo.pregunta] === campo.opcion) return <span style={{ ...estilo, fontWeight: 800 }}>X</span>;
  return null;
}

/**
 * EL PDF QUE VA A FIRMAR EL CLIENTE, con cada sitio resaltado: así sabe dónde
 * va a salir su firma antes de firmar. Si pdf.js no carga, el documento se ve
 * igual que antes (el visor del navegador) y la firma funciona igual.
 */
export function PdfConSitios({ url, campos, valores, lang = 'en' }) {
  const { pdf, error, cargando } = usePdf(null, url);
  const lista = Array.isArray(campos) ? campos : [];
  const paginas = pdf ? Math.min(pdf.numPages, MAX_PAGINAS_DEL_EDITOR) : 0;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '.5rem', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <FileText size={16} style={{ color: '#00d4ff' }} />
          <strong style={{ fontSize: 13, color: '#7aa3c0', textTransform: 'uppercase', letterSpacing: '.5px' }}>Document</strong>
        </div>
        <a href={url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#00d4ff', fontSize: 12, textDecoration: 'none' }}>
          <Download size={12} /> Open / download
        </a>
      </div>
      <div style={{ fontSize: 12, color: '#7aa3c0', marginBottom: '.75rem' }}>
        The highlighted boxes show where your signature, initials, name and date will be placed. / Los recuadros marcan dónde saldrán su firma, iniciales, nombre y fecha.
      </div>
      {cargando && <div style={{ color: '#7aa3c0', textAlign: 'center', padding: '2rem' }}>Loading…</div>}
      {error ? (
        <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden' }}>
          <iframe src={url} title="Document to sign" style={{ width: '100%', height: 720, border: 'none', display: 'block', background: '#fff' }} />
        </div>
      ) : Array.from({ length: paginas }, (_, i) => i + 1).map((numero) => (
        <PaginaDelPdf key={numero} pdf={pdf} numero={numero}>
          {lista.filter((c) => c.page === numero).map((c) => (
            <CajaDeCampo key={c.id} campo={c} lang={lang} contenido={vistaDelValor(c, valores)} />
          ))}
        </PaginaDelPdf>
      ))}
    </div>
  );
}
